import { useCallback, useEffect, useState } from "react";
import {
  getMyReleasedDocuments,
  viewReleasedDocument,
} from "../api/inmateDocuments";
import InmateNav from "../components/InmateNav";
import StatusMessage from "../components/StatusMessage";
import { getErrorMessage } from "../utils/apiErrors";

function formatDate(value) {
  if (!value) {
    return "—";
  }

  return new Date(value).toLocaleString();
}

export default function InmateDocuments() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewingId, setViewingId] = useState(null);
  const [error, setError] = useState("");

  const user = JSON.parse(localStorage.getItem("legal_portal_user"));

  const receivedDocuments = documents.filter(
    (doc) => doc.direction !== "inmate_to_attorney"
  );

  const sentDocuments = documents.filter(
    (doc) => doc.direction === "inmate_to_attorney"
  );

  const loadDocuments = useCallback(async () => {
    setLoading(true);

    try {
      const res = await getMyReleasedDocuments(user.inmate_id);
      setDocuments(res.data.data || []);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load your documents."));
    } finally {
      setLoading(false);
    }
  }, [user.inmate_id]);

  const handleView = async (documentId) => {
    if (viewingId) return;
    setViewingId(documentId);
    setError("");
    try {
      const res = await viewReleasedDocument(user.inmate_id, documentId);

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      window.open(url, "_blank", "noopener,noreferrer");

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 60000);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to open this document securely."));
    } finally {
      setViewingId(null);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  return (
    <div className="app-shell">
      <div className="topbar d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <div className="brand-mark">CV</div>

          <div>
            <h5 className="mb-0">CounselVault</h5>
            <div className="small opacity-75">Inmate Document Access</div>
          </div>
        </div>

        <InmateNav />
      </div>

      <main className="page-wrap">
        <StatusMessage type="warning">{error}</StatusMessage>
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div>
            <h2 className="mb-1">My Legal Documents</h2>
            <p className="text-muted mb-0">
              View documents received from attorneys and documents you sent.
            </p>
          </div>

          <div className="d-flex gap-3">
            <div className="stat-card text-center">
              <div className="text-muted small">Received</div>
              <h3 className="mb-0">{receivedDocuments.length}</h3>
            </div>

            <div className="stat-card text-center">
              <div className="text-muted small">Sent</div>
              <h3 className="mb-0">{sentDocuments.length}</h3>
            </div>
          </div>
        </div>

        <div className="portal-card">
          <div className="portal-card-header d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">Received Documents</h5>
              <div className="text-muted small">
                Documents released to you by approved attorneys.
              </div>
            </div>

            <button className="btn btn-sm btn-outline-secondary" onClick={loadDocuments}>
              Refresh
            </button>
          </div>

          <div className="portal-card-body">
            {loading ? (
              <p className="mb-0">Loading documents...</p>
            ) : receivedDocuments.length === 0 ? (
              <div className="text-center py-5">
                <h5>No received documents yet</h5>
                <p className="text-muted mb-0">
                  Documents released to you will appear here.
                </p>
              </div>
            ) : (
              <div className="row g-3">
                {receivedDocuments.map((doc) => (
                  <div className="col-md-6 col-xl-4" key={doc.id}>
                    <div className="portal-card h-100">
                      <div className="portal-card-body">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <span className="status-pill status-released">
                            Received
                          </span>

                          <div className="text-muted small">
                            #{doc.id}
                          </div>
                        </div>

                        <h5 className="mb-2">
                          {doc.title || "Untitled Document"}
                        </h5>

                        <p className="text-muted small mb-3">
                          {doc.original_filename || "Legal document PDF"}
                        </p>

                        <div className="text-muted small mb-4">
                          Released: {formatDate(doc.released_at)}
                        </div>

                        <button
                          className="btn btn-dark w-100"
                          onClick={() => handleView(doc.id)}
                          disabled={viewingId !== null}
                        >
                          View PDF
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="portal-card mt-4">
          <div className="portal-card-header">
            <h5 className="mb-0">Sent Documents</h5>
            <div className="text-muted small">
              Documents you sent to approved attorneys.
            </div>
          </div>

          <div className="portal-card-body">
            {loading ? (
              <p className="mb-0">Loading sent documents...</p>
            ) : sentDocuments.length === 0 ? (
              <div className="text-center py-5">
                <h5>No sent documents yet</h5>
                <p className="text-muted mb-0">
                  Documents you send to approved attorneys will appear here.
                </p>
              </div>
            ) : (
              <div className="row g-3">
                {sentDocuments.map((doc) => (
                  <div className="col-md-6 col-xl-4" key={doc.id}>
                    <div className="portal-card h-100">
                      <div className="portal-card-body">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <span className="status-pill bg-info-subtle text-info-emphasis">
                            Sent
                          </span>

                          <div className="text-muted small">
                            #{doc.id}
                          </div>
                        </div>

                        <h5 className="mb-2">
                          {doc.title || "Untitled Document"}
                        </h5>

                        <p className="text-muted small mb-3">
                          {doc.original_filename || "Legal document PDF"}
                        </p>

                        <div className="text-muted small mb-2">
                          To: {doc.attorney_profile?.user?.name || "Attorney"}
                        </div>

                        <div className="text-muted small mb-4">
                          Sent: {formatDate(doc.created_at)}
                        </div>

                        <button
                          className="btn btn-outline-dark w-100"
                          onClick={() => handleView(doc.id)}
                          disabled={viewingId !== null}
                        >
                          View PDF
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
